import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { AnimatePresence } from 'motion/react'
import { dateFormats } from '@mindfulchefuk/lib/dateFormats'

import { Box, Button, Flex, Text } from '@mindfulchefuk/design-system'
import { Icon } from '@mindfulchefuk/design-system/Icon'
import {
  CartDrawerWrapper,
  CloseButton,
} from '@mindfulchefuk/features/Cart/components/CartDrawer/CartDrawerWrapper'
import { Recipes } from '@mindfulchefuk/features/Cart/components/CartDrawer/Recipes'

import { RootState } from '@mindfulchefuk/types/store'
import { getMinimumMaximumRecipesForPortion } from '@mindfulchefuk/services/getMinimumMaximumRecipesForPortion'
import getPaymentDate from '@mindfulchefuk/helpers/js/getPaymentDate'

import {
  Article,
  Footer,
} from '@mindfulchefuk/features/Cart/components/CartDrawer/CardDrawer.styles'
import {
  Recipe,
  RecipeWithQuantity,
} from '@mindfulchefuk/features/Recipes/interfaces'
import { PORTION_COUNT_DEFAULT } from '@mindfulchefuk/constants'
import { OnboardingEmptyCart } from '../OnboardingEmptyCart/OnboardingEmptyCart'

export type TOnboardingCardDrawer = {
  addRecipe: (item: Recipe) => void
  isDrawerOpen?: boolean
  onPressContinue: () => void
  orderShippingAmount?: number
  originalTotal?: number
  recipes: RecipeWithQuantity[]
  removeRecipe: (item: Recipe) => void
  toggleDrawerOpen: () => void
  total: number
}

export const DeprecatedOnboardingCartDrawer = ({
  addRecipe,
  isDrawerOpen,
  onPressContinue,
  orderShippingAmount = 0,
  originalTotal,
  recipes = [],
  removeRecipe,
  toggleDrawerOpen,
  total,
}: TOnboardingCardDrawer) => {
  const { items, deliveryDate, portionCount } = useSelector(
    (store: RootState) => ({
      items: store.basket.items,
      deliveryDate: store.basket.selectedDeliveryDate,
      portionCount: store.basket.portionCount,
    }),
    shallowEqual
  )

  if (!isDrawerOpen) {
    return null
  }

  const recipePaymentDate = getPaymentDate(deliveryDate)

  const { minimumRecipes, maximumRecipes } =
    getMinimumMaximumRecipesForPortion(portionCount)

  const selectedBasketPortionCount = portionCount || PORTION_COUNT_DEFAULT
  const hasRecipes = !!recipes.length
  const cartIsEmpty = recipes.length === 0
  const minRecipesNotReached = items.length < minimumRecipes

  const renderFooterButton = () => {
    let footerButtonText
    let footerButtonHandler

    if (minRecipesNotReached) {
      footerButtonText = 'Choose Recipes'
      footerButtonHandler = () => {
        toggleDrawerOpen()
      }
    } else {
      footerButtonText = 'Confirm Recipes'
      footerButtonHandler = onPressContinue
    }

    return (
      <Button
        variant="primary"
        size={['medium', 'large']}
        type="button"
        onClick={footerButtonHandler}
        fullWidth
        data-testid="footer-button"
      >
        {footerButtonText}
      </Button>
    )
  }

  return (
    <CartDrawerWrapper isOpen={isDrawerOpen} onClose={toggleDrawerOpen}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        borderColor="pepper-50"
        borderBottomWidth={1}
        marginBottom={0}
        paddingY={{ base: 12, sm: 24 }}
        paddingX={24}
      >
        <Text as="h3" variant="heading-sm" color="aubergine">
          Order for delivery on{' '}
          {dateFormats.fullWeekdayAbbvMonthDay(deliveryDate)}
        </Text>

        <Flex
          height="100%"
          alignItems={{ base: 'flex-start', sm: 'center' }}
          paddingY={{ base: 0, sm: 0 }}
          flexDirection="column"
        >
          <CloseButton
            data-testid="cart-drawer-close-button"
            whileTap={{ scale: 0.9 }}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault()
              toggleDrawerOpen()
            }}
          >
            <Icon type="cross" size={16} color="aubergine" />
          </CloseButton>
        </Flex>
      </Flex>
      <AnimatePresence mode="wait">
        <Article layout>
          {cartIsEmpty && <OnboardingEmptyCart />}
          {hasRecipes && (
            <Recipes
              isOpen={isDrawerOpen}
              basketOriginalTotal={originalTotal}
              basketTotal={total}
              maxRecipes={maximumRecipes}
              minRecipes={minimumRecipes}
              paymentDate={recipePaymentDate}
              shippingPrice={orderShippingAmount}
              portionCountPerMeal={selectedBasketPortionCount}
              removeItem={(recipeId) => {
                removeRecipe(recipes.find((r) => r.id === recipeId))
              }}
              addItem={(recipeId) => {
                addRecipe(recipes.find((r) => r.id === recipeId))
              }}
              recipes={recipes}
              totalRecipes={recipes.reduce(
                (count, recipe) => count + recipe.quantity,
                0
              )}
              key="recipes"
              orderIsCompleted={false}
            />
          )}
        </Article>
      </AnimatePresence>
      <Footer layout>
        <Box
          paddingY={{ base: 12, sm: 24 }}
          paddingX={24}
          backgroundColor="white"
        >
          {renderFooterButton()}
        </Box>
      </Footer>
    </CartDrawerWrapper>
  )
}
